import React, { useEffect, useMemo, useState } from "react";
import "./ContactModal.css";
import { submitCareerApplication, submitLegacyContact } from "../utils/api";
import CaptchaBadge from "../components/CaptchaBadge";

const ContactModal = ({ onClose, variant = "home", title }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [resume, setResume] = useState(null);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  const resolvedTitle = useMemo(() => {
    if (title) return title;
    return variant === "internship"
      ? "Please fill in the details below"
      : "Contact Us";
  }, [title, variant]);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 6; i += 1) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    setCaptchaInput("");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const validate = () => {
    const nextErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\d{10}$/;

    if (!name.trim()) nextErrors.name = "Name is required.";
    else if (!nameRegex.test(name.trim()))
      nextErrors.name = "Only letters allowed.";

    if (!email.trim()) nextErrors.email = "Email is required.";
    else if (!emailRegex.test(email.trim()))
      nextErrors.email = "Enter a valid email.";

    if (!phone.trim()) nextErrors.phone = "Phone number is required.";
    else if (!phoneRegex.test(phone.trim()))
      nextErrors.phone = "Must be 10 digits.";

    if (variant === "internship") {
      if (!resume) nextErrors.resume = "Resume PDF is required.";
      else if (resume.type !== "application/pdf")
        nextErrors.resume = "File must be a PDF.";
      else if (resume.size > 128 * 1024)
        nextErrors.resume = "File size must be <= 128KB.";
    }

    if (message.length > 250) {
      nextErrors.message = "Message must not exceed 250 characters.";
    }

    if (!captchaInput.trim()) nextErrors.captcha = "CAPTCHA is required.";
    else if (captchaInput.trim().toUpperCase() !== captchaCode) {
      nextErrors.captcha = "CAPTCHA code is incorrect.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    if (variant === "internship") {
      try {
        const res = await submitCareerApplication({
          name,
          email,
          phone,
          message,
          file: resume,
        });

        if (res) {
          setSuccessMsg("✅ Your application has been submitted successfully!");
          setName("");
          setEmail("");
          setPhone("");
          setMessage("");
          setResume(null);
          generateCaptcha();
          setTimeout(() => onClose(), 1500);
        } else {
          alert("Something went wrong. Please try again.");
        }
      } catch (error) {
        console.error("Career application error", error);
        alert(error.message || "⚠️ Server error. Try again later.");
      }
      return;
    }

    try {
      const data = await submitLegacyContact({
        name,
        email,
        phone,
        message,
        variant,
      });

      if (data) {
        setSuccessMsg("✅ Your message has been submitted successfully!");
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
        generateCaptcha();
        setTimeout(() => onClose(), 1500);
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("⚠️ Server error. Try again later.");
    }
  };

  const renderHomeForm = () => (
    <>
      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {errors.name && <p className="error">{errors.name}</p>}

      <input
        type="email"
        placeholder="Email Address"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      {errors.email && <p className="error">{errors.email}</p>}

      <input
        type="tel"
        placeholder="Contact Number"
        value={phone}
        onChange={(event) => {
          const value = event.target.value.replace(/\D/g, '');
          setPhone(value);
        }}
        maxLength="10"
      />
      {errors.phone && <p className="error">{errors.phone}</p>}

      <textarea
        placeholder="Your message (Max 250 chars)"
        value={message}
        onChange={(event) => setMessage(event.target.value.slice(0, 250))}
      ></textarea>
      {errors.message && <p className="error">{errors.message}</p>}
      <div className="modal-field">
        <div className="modal-captcha-row">
          <CaptchaBadge
            code={captchaCode}
            width={90}
            height={38}
            bgColor="#1e293b"
            textColor="#f8fafc"
            fontSize={18}
          />
          <button
            type="button"
            className="modal-captcha-refresh"
            onClick={generateCaptcha}
            aria-label="Refresh CAPTCHA"
          >
            ↻
          </button>
          <input
            type="text"
            placeholder="Enter CAPTCHA"
            value={captchaInput}
            onChange={(event) => setCaptchaInput(event.target.value)}
            onPaste={(e) => e.preventDefault()}
            autoComplete="off"
            aria-label="Enter CAPTCHA"
          />
          <button type="submit" className="modal-submit modal-submit-inline">
            Submit
          </button>
        </div>
        {errors.captcha && <p className="error">{errors.captcha}</p>}
      </div>
    </>
  );

  const renderInternshipForm = () => (
    <>
      <input
        type="text"
        placeholder="Your name *"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {errors.name && <p className="error">{errors.name}</p>}

      <input
        type="email"
        placeholder="Your email *"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      {errors.email && <p className="error">{errors.email}</p>}

      <input
        type="tel"
        placeholder="Your phone number *"
        value={phone}
        onChange={(event) => {
          const value = event.target.value.replace(/\D/g, '');
          setPhone(value);
        }}
        maxLength="10"
      />
      {errors.phone && <p className="error">{errors.phone}</p>}

      <textarea
        placeholder="Message.."
        value={message}
        onChange={(event) => setMessage(event.target.value.slice(0, 250))}
      ></textarea>
      {errors.message && <p className="error">{errors.message}</p>}

      <div className="modal-field">
        <label className="modal-upload-label">Upload Resume (PDF) *</label>
        <input
          type="file"
          placeholder="Upload Resume (PDF) *"
          accept="application/pdf"
          onChange={(event) => setResume(event.target.files[0])}
        />
      </div>
      {errors.resume && <p className="error">{errors.resume}</p>}

      <div className="modal-field">
        <div className="modal-captcha-row">
          <CaptchaBadge
            code={captchaCode}
            width={90}
            height={38}
            bgColor="#1e293b"
            textColor="#f8fafc"
            fontSize={18}
          />
          <button
            type="button"
            className="modal-captcha-refresh"
            onClick={generateCaptcha}
            aria-label="Refresh CAPTCHA"
          >
            ↻
          </button>
          <input
            type="text"
            placeholder="Enter CAPTCHA"
            value={captchaInput}
            onChange={(event) => setCaptchaInput(event.target.value)}
            onPaste={(e) => e.preventDefault()}
            autoComplete="off"
            aria-label="Enter CAPTCHA"
          />
          <button type="submit" className="modal-submit modal-submit-inline">
            Submit
          </button>
        </div>
        {errors.captcha && <p className="error">{errors.captcha}</p>}
      </div>
    </>
  );

  const cardClassName = `modal-card`;
  const headerClassName = `modal-header`;
  const formClassName = `modal-form`;

  return (
    <div className="modal-overlay">
      <div className={cardClassName} role="dialog" aria-modal="true">
        <div className={headerClassName}>
          <h2>{resolvedTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="modal-close"
          >
            &times;
          </button>
        </div>

        <form className={formClassName} onSubmit={handleSubmit} noValidate>
          {variant === "internship" ? renderInternshipForm() : renderHomeForm()}
        </form>

        {successMsg && <p className="success">{successMsg}</p>}
      </div>
    </div>
  );
};

export default ContactModal;
